import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, Users, Minus, Plus, CheckCircle2, UserRound, UtensilsCrossed, Share2, CalendarPlus, Star } from "lucide-react";
import { toast } from "sonner";
import { fr } from "date-fns/locale";
import { api, formatApiError } from "@/lib/api";
import { shareOrCopy } from "@/lib/share";
import { downloadCalendarEvent } from "@/lib/calendar";
import { Calendar } from "@/components/ui/calendar";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";

const inputCls =
  "w-full bg-white border border-[#6B1226]/15 rounded-xl px-4 py-3 text-base outline-none focus:border-[#6B1226]/50 transition-colors";

const dayLabel = (iso) => {
  const d = new Date(iso + "T12:00:00");
  const s = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export default function Reserve() {
  const { user, loading } = useAuth();
  const [config, setConfig] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [guests, setGuests] = useState(2);
  const [note, setNote] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [existing, setExisting] = useState(null);
  const [calOpen, setCalOpen] = useState(false);

  // Pré-remplissage depuis le compte (modifiable : on peut réserver pour quelqu'un d'autre)
  useEffect(() => {
    if (user) {
      setFirstName((v) => v || user.first_name || "");
      setLastName((v) => v || user.last_name || "");
      setPhone((v) => v || user.phone || "");
    }
  }, [user]);

  const shareDone = () =>
    shareOrCopy({
      title: "Table réservée — Le Bistroquet, Belfort",
      text: `J'ai réservé une table pour ${done.guests} au Bistroquet (Belfort) — ${dayLabel(done.date)} à ${done.time.replace(":", "h")}. Rejoins-nous !`,
      url: `${window.location.origin}/api/share/reservation/${done.id}`,
      image: `${window.location.origin}/api/share/reservation/${done.id}/card.jpg`,
      copiedMsg: "Lien de réservation copié — partagez-le à vos convives",
    });

  const addToCalendar = () => {
    downloadCalendarEvent({
      title: `Table au Bistroquet — ${done.guests} couvert${done.guests > 1 ? "s" : ""}`,
      description: `Réservation pour ${done.guests} au nom de ${done.customer_name} (${done.customer_phone}).`,
      location: "Le Bistroquet, Belfort",
      date: done.date,
      time: done.time,
      durationMin: 90,
    });
    toast.success("Événement téléchargé — ouvrez-le pour l'ajouter à votre agenda");
  };

  useEffect(() => {
    api.get("/reservations/slots").then((res) => setConfig(res.data)).catch(() => setConfig({ slots: { midi: [], soir: [] }, max_guests: 12, max_days_ahead: 60 }));
  }, []);

  // Réservations déjà enregistrées : l'admin (restaurant) voit toutes les tables à venir,
  // le client voit les siennes. Affichées dans l'onglet Réserver, avant le formulaire.
  // hasPast : le client a déjà dîné chez nous -> invitation courtoise à noter sur Google.
  const [hasPast, setHasPast] = useState(false);
  const loadExisting = () => {
    if (!user) {
      setExisting(null);
      setHasPast(false);
      return;
    }
    const req = user.role === "admin" ? api.get("/admin/reservations") : api.get("/reservations/mine");
    req
      .then((res) => {
        setExisting(res.data.filter((r) => r.status !== "annulee" && r.status !== "honoree" && r.date >= today));
        setHasPast(res.data.some((r) => r.status !== "annulee" && (r.status === "honoree" || r.date < today)));
      })
      .catch(() => setExisting([]));
  };
  useEffect(loadExisting, [user]);

  const today = new Date().toLocaleDateString("sv-SE");
  const maxDate = new Date(Date.now() + (config?.max_days_ahead || 60) * 86400000).toLocaleDateString("sv-SE");
  const maxGuests = config?.max_guests || 12;

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      setError("Choisissez la date et l'heure de votre venue.");
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError("Indiquez le prénom et le nom de la personne qui réserve.");
      return;
    }
    // même règle que le serveur : portable français 06/07 (espaces, points et +33 acceptés)
    const phoneNormalized = phone.replace(/[\s.\-]/g, "").replace(/^0033/, "+33");
    if (!/^(\+33|0)[67]\d{8}$/.test(phoneNormalized)) {
      setError("Indiquez un numéro de portable valide (06 ou 07…).");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await api.post("/reservations", {
        date,
        time,
        guests,
        note: note || undefined,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phoneNormalized,
      });
      setDone(res.data);
      toast.success("Votre table est réservée !");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reserve-page">
      <section className="app-container px-4 sm:px-8 pt-5 sm:pt-7 pb-12 sm:pb-16">
        <Reveal>
          <p className="eyebrow mb-2">Le Bistroquet — Belfort</p>
          <h1 className="font-display text-4xl font-semibold text-[#1C1917]">Réserver une table</h1>
          <p className="mt-2 text-sm text-[#57534E] max-w-xl">
            En 30 secondes, sans appeler : choisissez votre jour, votre heure et le nombre de convives. Nous vous confirmons immédiatement.
          </p>
        </Reveal>

        {!done && user && existing && existing.length > 0 && (
          <Reveal delay={0.05}>
            <div className="mt-7 max-w-xl" data-testid="reserve-existing">
              <h2 className="font-display text-xl font-semibold text-[#1C1917]">
                {user.role === "admin" ? "Réservations enregistrées" : "Vos réservations à venir"}
              </h2>
              <ul className="mt-3 space-y-2.5">
                {existing.map((r, i) => (
                  <li key={r.id} className="bg-white rounded-2xl border border-[#6B1226]/10 px-4 py-3 flex items-center justify-between gap-3" data-testid={`reserve-existing-${i}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1C1917]">
                        {dayLabel(r.date)} à {r.time.replace(":", "h")}
                      </p>
                      <p className="text-xs text-[#57534E] mt-0.5">
                        {user.role === "admin" && <span className="font-semibold text-[#6B1226]">{r.customer_name} · </span>}
                        {r.guests} couvert{r.guests > 1 ? "s" : ""}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold ${
                        r.status === "confirmee"
                          ? "bg-[#2D5A27]/10 text-[#2D5A27]"
                          : r.status === "honoree"
                            ? "bg-[#57534E]/10 text-[#57534E]"
                            : "bg-[#8C6D23]/10 text-[#8C6D23]"
                      }`}
                    >
                      {r.status === "confirmee" ? "Confirmée" : r.status === "honoree" ? "Honorée" : "En attente"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        )}

        {!done && user && user.role !== "admin" && hasPast && (
          <Reveal delay={0.06}>
            <div className="mt-7 max-w-xl bg-white rounded-2xl border border-[#C5A059]/30 p-5 flex items-start gap-4" data-testid="reserve-review-card">
              <span className="shrink-0 w-10 h-10 rounded-full bg-[#F5EEDC] text-[#8C6D23] flex items-center justify-center">
                <Star size={18} />
              </span>
              <div className="min-w-0">
                <p className="font-display text-lg font-semibold text-[#1C1917]">Un immense merci pour votre visite !</p>
                <p className="text-sm text-[#57534E] mt-1 leading-relaxed">
                  Si vous avez apprécié votre moment au Bistroquet, quelques étoiles et un petit mot sur Google sont le plus beau
                  cadeau que vous puissiez nous faire — cela nous aide énormément à faire connaître notre table.
                </p>
                <a
                  href="https://www.google.com/search?q=avis+sur+le+bistroquet+belfort&hl=fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#8C6D23] text-[#FAF6F0] px-5 py-2.5 text-sm font-semibold hover:bg-[#6f5719] transition-colors"
                  data-testid="reserve-review-button"
                >
                  <Star size={14} /> Laisser un avis sur Google
                </a>
              </div>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.08}>
          {done ? (
            <div className="mt-8 max-w-xl bg-white rounded-3xl border border-[#6B1226]/10 card-shadow p-8 text-center" data-testid="reserve-success">
              <span className="mx-auto w-14 h-14 rounded-full bg-[#2D5A27]/10 text-[#2D5A27] flex items-center justify-center">
                <CheckCircle2 size={28} />
              </span>
              <h2 className="font-display text-2xl font-semibold text-[#1C1917] mt-4">À très vite, {done.customer_name?.split(" ")[0]} !</h2>
              <p className="mt-2 text-sm text-[#57534E]">
                Votre table pour <strong>{done.guests} couvert{done.guests > 1 ? "s" : ""}</strong> est réservée le{" "}
                <strong>{dayLabel(done.date)} à {done.time.replace(":", "h")}</strong>.
              </p>
              <p className="mt-1.5 text-xs text-[#A8A29E] italic">Un empêchement ? Vous pouvez annuler depuis votre profil, onglet « Mes réservations ».</p>
              <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-2.5 justify-center">
                <button
                  onClick={shareDone}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#6B1226] text-[#FAF6F0] px-6 py-3 text-sm font-bold hover:bg-[#580E1D] transition-colors"
                  data-testid="reserve-share-button"
                >
                  <Share2 size={15} /> Partager cette réservation
                </button>
                <button
                  onClick={addToCalendar}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#6B1226]/25 text-[#6B1226] px-6 py-3 text-sm font-semibold hover:bg-[#6B1226]/5 transition-colors"
                  data-testid="reserve-calendar-button"
                >
                  <CalendarPlus size={15} /> Ajouter à mon agenda
                </button>
                <Link to="/profil" className="rounded-full border border-[#6B1226]/25 text-[#6B1226] px-6 py-3 text-sm font-semibold hover:bg-[#6B1226]/5 transition-colors text-center" data-testid="reserve-success-profile">
                  Mes réservations
                </Link>
                <Link to="/" className="rounded-full border border-[#6B1226]/25 text-[#6B1226] px-6 py-3 text-sm font-semibold hover:bg-[#6B1226]/5 transition-colors text-center" data-testid="reserve-success-home">
                  Retour à l'accueil
                </Link>
              </div>
            </div>
          ) : loading ? (
            <p className="mt-10 text-sm text-[#57534E] italic">Chargement…</p>
          ) : !user ? (
            <div className="mt-8 max-w-xl bg-white rounded-3xl border border-[#6B1226]/10 card-shadow p-8" data-testid="reserve-auth-gate">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-[#6B1226]/10 flex items-center justify-center text-[#6B1226]">
                  <UserRound size={22} />
                </span>
                <p className="text-sm text-[#1C1917] leading-snug">
                  <span className="font-semibold">Un compte est nécessaire pour réserver.</span>
                  <br />
                  <span className="text-[#57534E] text-xs">Créez-le en 30 secondes — il sert aussi pour vos commandes de vins.</span>
                </p>
              </div>
              <Link
                to="/profil"
                className="mt-5 inline-flex w-full items-center justify-center rounded-full bg-[#6B1226] text-[#FAF6F0] py-3.5 text-sm font-bold hover:bg-[#580E1D] transition-colors"
                data-testid="reserve-login-button"
              >
                Me connecter / créer mon compte
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 max-w-xl bg-white rounded-3xl border border-[#6B1226]/10 card-shadow p-6 sm:p-8 space-y-5" data-testid="reserve-form">
              <div>
                <label className="flex items-center gap-2 text-xs font-bold text-[#1C1917] mb-2">
                  <UserRound size={15} className="text-[#6B1226]" /> Qui réserve ?
                </label>
                <div className="grid grid-cols-2 gap-2.5">
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Prénom"
                    autoComplete="given-name"
                    className={inputCls}
                    data-testid="reserve-firstname-input"
                  />
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Nom"
                    autoComplete="family-name"
                    className={inputCls}
                    data-testid="reserve-lastname-input"
                  />
                </div>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Portable (06 ou 07…)"
                  autoComplete="tel"
                  inputMode="tel"
                  className={`${inputCls} mt-2.5`}
                  data-testid="reserve-phone-input"
                />
                <p className="text-[0.65rem] text-[#A8A29E] italic mt-1.5">Le restaurant vous rappelle sur ce numéro en cas d'imprévu.</p>
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs font-bold text-[#1C1917] mb-2">
                  <CalendarDays size={15} className="text-[#6B1226]" /> Quel jour ?
                </label>
                <button
                  type="button"
                  onClick={() => setCalOpen((v) => !v)}
                  className={`${inputCls} text-left flex items-center justify-between gap-3 ${date ? "text-[#1C1917] font-semibold" : "text-[#A8A29E]"}`}
                  data-testid="reserve-date-button"
                  aria-expanded={calOpen}
                >
                  {date ? dayLabel(date) : "Choisir un jour"}
                  <CalendarDays size={16} className="text-[#6B1226] shrink-0" />
                </button>
                {calOpen && (
                  <div className="mt-2 bg-white rounded-xl border border-[#6B1226]/15 p-1.5 flex justify-center" data-testid="reserve-calendar">
                    <Calendar
                      mode="single"
                      locale={fr}
                      showOutsideDays={false}
                      selected={date ? new Date(date + "T12:00:00") : undefined}
                      defaultMonth={date ? new Date(date + "T12:00:00") : new Date()}
                      onSelect={(d) => {
                        if (!d) return;
                        setDate(d.toLocaleDateString("sv-SE"));
                        setError("");
                        setCalOpen(false);
                      }}
                      disabled={[{ dayOfWeek: [0, 6] }, { before: new Date(today + "T00:00:00") }, { after: new Date(maxDate + "T23:59:59") }]}
                      classNames={{
                        day_selected: "bg-[#6B1226] text-[#FAF6F0] hover:bg-[#6B1226] focus:bg-[#6B1226]",
                        day_today: "bg-[#F5EEDC] text-[#8C6D23] font-semibold",
                        day_disabled: "text-[#C8C2BA] opacity-50 line-through",
                      }}
                    />
                  </div>
                )}
                <p className="text-[0.65rem] text-[#A8A29E] italic mt-1.5">Ouvert du lundi au vendredi — samedi et dimanche grisés.</p>
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs font-bold text-[#1C1917] mb-2">
                  <Clock size={15} className="text-[#6B1226]" /> À quelle heure ?
                </label>
                {config && (
                  <div className="space-y-3">
                    {Object.entries(config.slots).map(([service, hours]) => (
                      <div key={service}>
                        <p className="eyebrow text-[0.6rem] mb-1.5">Service du {service}</p>
                        <div className="flex flex-wrap gap-2" data-testid={`reserve-slots-${service}`}>
                          {hours.map((h) => (
                            <button
                              type="button"
                              key={h}
                              onClick={() => setTime(h)}
                              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                                time === h
                                  ? "bg-[#6B1226] text-[#FAF6F0]"
                                  : "bg-[#FAF6F0] border border-[#6B1226]/20 text-[#1C1917] hover:border-[#6B1226]"
                              }`}
                              data-testid={`reserve-slot-${h.replace(":", "")}`}
                              aria-pressed={time === h}
                            >
                              {h.replace(":", "h")}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs font-bold text-[#1C1917] mb-2">
                  <Users size={15} className="text-[#6B1226]" /> Combien de convives ?
                </label>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setGuests((g) => Math.max(1, g - 1))}
                    disabled={guests <= 1}
                    className="w-11 h-11 rounded-full border border-[#6B1226]/25 text-[#6B1226] flex items-center justify-center hover:bg-[#6B1226]/5 transition-colors disabled:opacity-30"
                    data-testid="reserve-guests-minus"
                    aria-label="Un convive de moins"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="font-display text-2xl font-semibold text-[#1C1917] min-w-[3.5rem] text-center" data-testid="reserve-guests-count">
                    {guests}
                  </span>
                  <button
                    type="button"
                    onClick={() => setGuests((g) => Math.min(maxGuests, g + 1))}
                    disabled={guests >= maxGuests}
                    className="w-11 h-11 rounded-full border border-[#6B1226]/25 text-[#6B1226] flex items-center justify-center hover:bg-[#6B1226]/5 transition-colors disabled:opacity-30"
                    data-testid="reserve-guests-plus"
                    aria-label="Un convive de plus"
                  >
                    <Plus size={16} />
                  </button>
                  <p className="text-[0.7rem] text-[#A8A29E] italic">Au-delà de {maxGuests}, appelez-nous au restaurant.</p>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs font-bold text-[#1C1917] mb-2">
                  <UtensilsCrossed size={15} className="text-[#6B1226]" /> Une précision ? (facultatif)
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="Anniversaire, poussette, allergie, préférence de table…"
                  className={`${inputCls} resize-none`}
                  data-testid="reserve-note-input"
                />
              </div>

              {error && (
                <p className="text-sm text-[#6B1226] bg-[#6B1226]/5 border border-[#6B1226]/20 rounded-xl px-4 py-3" data-testid="reserve-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={busy || !date || !time}
                className="w-full rounded-full bg-[#6B1226] text-[#FAF6F0] py-4 text-sm font-bold tracking-wide hover:bg-[#580E1D] transition-colors disabled:opacity-50"
                data-testid="reserve-submit-button"
              >
                {busy ? "Réservation en cours…" : "Réserver ma table"}
              </button>
            </form>
          )}
        </Reveal>
      </section>
      <Footer />
    </div>
  );
}
